import { Box } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import Breadcrumbs from "components/Breadcrumbs";
import Link from "components/Link";
import NoDataAvailable from "components/NoDataAvailable";
import SEO from "components/SEO";
import SessionContext from "components/session/SessionContext";
import Text from "components/Text";
import { pagePadding } from "data/commonClasses";
import {
  capitalize,
  fetchFromAPI,
  fetchPost,
  getTranslationContent,
  handleRouter,
  isArray,
} from "functions";
import Image from "next/image";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import useStyles from "styles/News.Style";
import { twMerge } from "tailwind-merge";
import useTranslation from "translations/hooks/useTranslation";

export default function NewsPage(props) {
  const classes = useStyles();
  const router = useRouter();
  const { t } = useTranslation();

  const regex = /(style|class)=["']+(.*?)["']+/gm;

  let {
    session: { lang },
  } = useContext(SessionContext);
  let isAr = lang === "ar";

  let page = 1,
    category = "all";
  let params = router?.query?.params;

  if (isArray(params)) {
    if (params.length == 1) {
      if (parseInt(params[0])) {
        page = parseInt(params[0]);
        category = "all";
      } else {
        page = 1;
        category = params[0];
      }
    }
    if (params.length > 1) {
      page = parseInt(params[1]) ? parseInt(params[1]) : 1;
      category = params[0];
    }
  }

  const [news, setNews] = useState(props.news);
  const [newsCount, setNewsCount] = useState(Math.ceil(props.newsCount / 6));
  const [text, setText] = useState("");

  useEffect(() => {
    if (!isArray(params))
      handleRouter({ href: "/news/[...params]", as: "/news/1" });
    function fetchData() {
      let reqBody = {
        limit: 6,
        page,
        text,
        category: category == "all" ? "" : category,
        stripHtml: true,
        lang,
      };
      fetchFromAPI({ api: "news", reqBody }).then((response) => {
        let news = response.response.news;
        let newsCount = isArray(response.response.newsCount)
          ? response.response.newsCount[0].newsCount
          : response.response.newsCount;

        newsCount = Math.ceil(newsCount / 6);
        setNews(news);
        setNewsCount(newsCount);
      });
    }
    if (text) fetchData();
    else {
      setNews(props.news);
      setNewsCount(Math.ceil(props.newsCount / 6));
    }
  }, [text, props.news]); //eslint-disable-line react-hooks/exhaustive-deps

  var breadcrumbs = [
    { text: t("home"), href: "/" },
    {
      text: `${
        category == "all"
          ? ""
          : category == "announcements"
          ? t("announcements")
          : t(capitalize(category))
      }`,
    },
  ];

  return (
    <div className={twMerge("flex flex-col w-full min-h-screen", pagePadding)}>
      <SEO title={t("news")} description={t("newsDescription")} />
      <Breadcrumbs items={breadcrumbs} />
      <Text
        text={
          category == "announcements"
            ? t("announcements")
            : t(capitalize(category))
        }
        bold
        sectionTitle
        className="mt-5"
      />
      <div className={"bg-lightGrey p-5 rounded-button my-5"}>
        {!isArray(news) ? (
          <NoDataAvailable />
        ) : (
          <>
            <div
              className={
                "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 place-items-start gap-5"
              }
            >
              {news.map((newsItem) => (
                <Link
                  href="/news/article/[slug]"
                  as={`/news/article/${newsItem.slug}`}
                  key={newsItem._id}
                >
                  <a>
                    <div className="w-full flex flex-col ">
                      <div className={"mb-5 aspect-square"}>
                        <Image
                          src={
                            newsItem.image
                              ? `${newsItem.image}`
                              : "/placeholder.jpg"
                          }
                          alt="News Image"
                          layout="intrinsic"
                          objectFit="cover"
                          className="aspect-square"
                          height={480}
                          width={480}
                          style={{
                            borderRadius: "12px",
                            borderWidth: "2px",
                            borderColor: "#E8E3CF",
                            borderStyle: "solid",
                          }}
                        />
                      </div>
                        <Text
                        className="text-ellipsis line-clamp-3 max-w-72 md:max-w-96"
                          bold
                          subtitle
                          text={getTranslationContent(newsItem, "title", lang)}
                        />
                    </div>
                  </a>
                </Link>
              ))}
            </div>
            {newsCount > 1 ? (
              <Box className={classes.paginationWrapper}>
                <Pagination
                  page={parseInt(page)}
                  count={newsCount}
                  siblingCount={0}
                  onChange={(event, value) =>
                    handleRouter({
                      href: `/news/[...params]`,
                      as: `/news/${
                        category == "all" ? value : category + "/" + value
                      }`,
                    })
                  }
                />
              </Box>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export async function getServerSideProps(context) {
  let { lang, params = [] } = context?.query;
  let page = 1,
    category = "";
  if (isArray(params)) {
    if (params.length == 1) {
      if (parseInt(params[0])) {
        page = parseInt(params[0]);
        category = "";
      } else {
        page = 1;
        category = params[0];
      }
    }
    if (params.length > 1) {
      page = parseInt(params[1]) ? parseInt(params[1]) : 1;
      category = params[0];
    }
  }
  let news = [];
  let response = {};
  let newsCount = 0;
  let reqBody = {
    limit: 6,
    page,
    category,
    stripHtml: true,
    lang,
  };
  context.req
    ? (response = await fetchPost({ apiName: "news", reqBody }))
    : (response = await fetchFromAPI({ api: "news", reqBody }));
  news = response?.response?.news;
  newsCount = isArray(response.response.newsCount)
    ? response?.response?.newsCount[0]?.newsCount
    : response?.response?.newsCount;
  return { props: { news, newsCount } };
}
